export interface SectionFinalCTAProps {
  headline?: string;
  accentLine?: string;
  subheadline?: string;
  image?: string;
  primaryCTA?: string;
}

const DEFAULT_IMAGE = 'https://public.readdy.ai/ai/img_res/c3edfe04a37999ba4dc2871d8773365f.jpg';

export default function SectionFinalCTA({ headline, accentLine, subheadline, image, primaryCTA }: SectionFinalCTAProps) {
  const scrollToEstimate = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('estimate')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative py-28 md:py-36 overflow-hidden">
      <div className="absolute inset-0">
        <img src={image || DEFAULT_IMAGE} alt="Mississippi Gulf Coast roofing" className="w-full h-full object-cover object-center" />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-ink/80 via-ink/78 to-ink/95"></div>
      <div className="absolute inset-0 bg-gradient-to-r from-ink/45 via-transparent to-ink/45"></div>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gold/35 to-transparent"></div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 text-center">
        <div className="flex items-center justify-center gap-4 mb-7">
          <span className="w-10 h-px bg-gold/60"></span>
          <span className="text-[10px] font-bold tracking-super uppercase text-gold/75">Protect Your Investment</span>
          <span className="w-10 h-px bg-gold/60"></span>
        </div>

        <h2 className="text-white leading-none mb-7">
          <span className="block font-serif italic text-2xl md:text-3xl lg:text-4xl text-white/50 mb-3">
            {accentLine || "Your roof is your home's first defense."}
          </span>
          <span className="block font-condensed font-bold text-6xl md:text-8xl uppercase tracking-tighter leading-none">
            {headline || 'PROTECT IT.'}
          </span>
          <span className="block font-serif italic text-2xl md:text-3xl text-gold mt-3">Built to Last.</span>
        </h2>

        <p className="text-white/50 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-12 font-light">
          {subheadline || "Don\u2019t wait for a small issue to become a major expense. Our GAF Certified team delivers roofing solutions built for Gulf Coast conditions \u2014 backed by full warranty."}
        </p>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-14">
          <a href="#estimate" onClick={scrollToEstimate}
            className="inline-flex items-center justify-center gap-3 bg-gold text-ink font-bold text-[13px] tracking-widest uppercase px-12 py-5 rounded hover:bg-gold-light transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-calendar-check-line"></i>
            {primaryCTA || 'Request a Free Estimate'}
          </a>
          <a href="tel:2285473887"
            className="inline-flex items-center justify-center gap-3 border border-white/20 text-white font-semibold text-[13px] tracking-widest uppercase px-12 py-5 rounded hover:border-gold/50 hover:text-gold transition-colors cursor-pointer whitespace-nowrap">
            <i className="ri-phone-fill text-gold"></i>(228) 547-3887
          </a>
        </div>

        <div className="flex flex-wrap justify-center gap-8 pt-10 border-t border-white/8">
          {[
            { icon: 'ri-shield-check-fill', label: 'Licensed & Insured' },
            { icon: 'ri-medal-2-fill', label: 'GAF Certified' },
            { icon: 'ri-award-fill', label: 'Fortification Certified' },
            { icon: 'ri-checkbox-circle-fill', label: 'Free Inspections' },
          ].map(t => (
            <span key={t.label} className="flex items-center gap-2 text-[10px] font-semibold tracking-widest uppercase text-white/28">
              <i className={`${t.icon} text-gold/70 text-xs`}></i>
              {t.label}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}
